import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Route, PlayCircle, Phone, LayoutDashboard, FileCheck2, ArrowRight } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Section";
import InteractionJourney from "../components/demo/InteractionJourney";
import RiskSimulator from "../components/demo/RiskSimulator";
import LogbergDemo from "../components/demo/LogbergDemo";
import HeimdallDashboard from "../components/demo/HeimdallDashboard";
import EvidenceRecord from "../components/demo/EvidenceRecord";
import { Button } from "../components/ui/button";
import { useDemo } from "../demo/DemoContext";

const STEPS = [
  { key: "journey", label: "Interaction journey", icon: Route },
  { key: "simulator", label: "Test an interaction", icon: PlayCircle },
  { key: "logberg", label: "Lögberg call review", icon: Phone },
  { key: "heimdall", label: "Heimdall console", icon: LayoutDashboard },
  { key: "evidence", label: "Evidence record", icon: FileCheck2 },
];

export default function InteractiveDemo() {
  const { resetDemo } = useDemo();
  const [active, setActive] = useState("journey");
  const [focusId, setFocusId] = useState(null);
  const [evidenceId, setEvidenceId] = useState(null);
  const topRef = useRef(null);

  useEffect(() => {
    document.title = "Interactive Demo | norse3";
    const hash = window.location.hash.replace("#", "");
    if (STEPS.some((s) => s.key === hash)) setActive(hash);
  }, []);

  const go = (key) => {
    setActive(key);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openHeimdall = (id) => { setFocusId(id); go("heimdall"); };
  const generate = (id) => { setEvidenceId(id); go("evidence"); };

  return (
    <div>
      <PageHero
        eyebrow="Interactive demo"
        title="See norse3 in action."
        subtitle="Follow one interaction from inspection to evidence. Test a synthetic scenario, watch the decision, open it in Heimdall, review it and generate a sample evidence record — all connected."
      >
        <div className="flex flex-wrap gap-3">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => go("simulator")}>
            Test an interaction <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <Button variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={resetDemo}>
            Reset demonstration
          </Button>
        </div>
      </PageHero>

      <div ref={topRef} />

      {/* Stepper */}
      <div className="sticky top-16 z-30 border-b border-white/10 bg-navy-950/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const on = active === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => go(s.key)}
                  aria-current={on}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    on ? "bg-primary/12 text-foreground border border-primary/40" : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
                  }`}
                >
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[0.7rem] ${on ? "bg-primary text-primary-foreground" : "bg-white/10 text-muted-foreground"}`}>{i + 1}</span>
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {active === "journey" && (
            <Reveal>
              <h2 className="text-2xl font-bold text-foreground">The live interaction journey</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">Click any stage to see what enters it, what norse3 does, the decision, what is recorded and where humans review.</p>
              <div className="mt-6"><InteractionJourney variant="full" /></div>
              <div className="mt-6"><Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => go("simulator")}>Continue: test an interaction <ArrowRight className="ml-1.5 h-4 w-4" /></Button></div>
            </Reveal>
          )}

          {active === "simulator" && (
            <Reveal>
              <h2 className="text-2xl font-bold text-foreground">Test an interaction</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">Choose a synthetic scenario and run it. You cannot enter your own text — this prevents any personal or confidential information being submitted.</p>
              <div className="mt-6"><RiskSimulator onOpenHeimdall={openHeimdall} onGenerate={generate} /></div>
            </Reveal>
          )}

          {active === "logberg" && (
            <Reveal>
              <h2 className="text-2xl font-bold text-foreground">Lögberg call review</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">A fictional firm and fully synthetic calls. Select a call, review the flagged moments, record a reviewer decision and capture evidence. FCA-aligned and reviewer-controlled.</p>
              <div className="mt-6"><LogbergDemo onGenerate={generate} /></div>
            </Reveal>
          )}

          {active === "heimdall" && (
            <Reveal>
              <h2 className="text-2xl font-bold text-foreground">Heimdall console</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">Filter and search seeded synthetic incidents, open one, review the interaction, add a decision and generate evidence. Changes stay in your browser session only.</p>
              <div className="mt-6"><HeimdallDashboard focusId={focusId} onGenerate={generate} /></div>
            </Reveal>
          )}

          {active === "evidence" && (
            <Reveal>
              <h2 className="text-2xl font-bold text-foreground">Sample evidence record</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">A clean preview of the evidence a handled interaction produces. Every generated document is clearly watermarked as an illustrative demonstration.</p>
              <div className="mt-6"><EvidenceRecord incidentId={evidenceId} onBack={() => go("heimdall")} /></div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-foreground">Ready to see it against your own workflows?</h2>
          <p className="mt-3 text-muted-foreground">Book a walkthrough and we will map the interaction path, agree policies and define a scoped pilot.</p>
          <Button asChild size="lg" className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
